import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import {
  createDefaultFileListFilters,
  createDefaultFileListSort,
  normalizeFileListFilters,
  normalizeFileListSort,
} from '../config/fileListView';

const FILE_LIST_VIEW_STORAGE_KEY = 'exiftrator.file-list-view.v1';

const AppStateContext = createContext();
const AppActionsContext = createContext();

// Split context: state values change frequently, setters are stable
export function AppProvider({ children }) {
  const [files, setFiles] = useState([]);
  const [selectedIndices, setSelectedIndices] = useState(new Set());
  const [primaryIndex, setPrimaryIndex] = useState(-1);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [pendingChanges, setPendingChanges] = useState({});
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [calendarSelectedDate, setCalendarSelectedDate] = useState(null);
  const [calendarSelectedHour, setCalendarSelectedHour] = useState(null);
  const [calendarPanelExpanded, setCalendarPanelExpanded] = useState(false);
  const [calendarViewMode, setCalendarViewMode] = useState("monthly");
  const [editorRightPanelOpen, setEditorRightPanelOpen] = useState(true);
  const [calendarRightPanelOpen, setCalendarRightPanelOpen] = useState(true);
  const [editorMobileLeftOpen, setEditorMobileLeftOpen] = useState(false);
  const [editorMobileRightOpen, setEditorMobileRightOpen] = useState(false);
  const [calendarMobileLeftOpen, setCalendarMobileLeftOpen] = useState(false);
  const [calendarMobileRightOpen, setCalendarMobileRightOpen] = useState(false);
  // Cell selection range for table view (rectangular selection)
  const [selectedCellRange, setSelectedCellRange] = useState(null);
  // Cell selection anchor and end for table view (persists across view switches)
  const [selectionAnchor, setSelectionAnchor] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  // Focused cell for table view (persists across view switches)
  const [focusedCell, setFocusedCell] = useState(null);
  // Location modal open state (global so any component can trigger it)
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  // Preset modal open state (global so any component can trigger it)
  const [presetModalOpen, setPresetModalOpen] = useState(false);
  // Keyboard shortcuts modal open state + source view tab
  const [keyboardShortcutsModalOpen, setKeyboardShortcutsModalOpen] = useState(false);
  const [keyboardShortcutsInitialView, setKeyboardShortcutsInitialView] = useState("editor");
  const [fileListSort, setFileListSort] = useState(createDefaultFileListSort);
  const [fileListFilters, setFileListFilters] = useState(createDefaultFileListFilters);
  const [tableVisibleRowMap, setTableVisibleRowMap] = useState([]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(FILE_LIST_VIEW_STORAGE_KEY);
      if (!stored) return;

      const parsed = JSON.parse(stored);
      setFileListSort(normalizeFileListSort(parsed.sort));
      setFileListFilters(normalizeFileListFilters(parsed.filters));
    } catch (error) {
      console.warn('Failed to load file list view preferences', error);
    }
  }, []);

  useEffect(() => {
    try {
      const payload = JSON.stringify({
        sort: normalizeFileListSort(fileListSort),
        filters: normalizeFileListFilters(fileListFilters),
      });
      localStorage.setItem(FILE_LIST_VIEW_STORAGE_KEY, payload);
    } catch (error) {
      console.warn('Failed to save file list view preferences', error);
    }
  }, [fileListSort, fileListFilters]);

  // State context - changes when any state value changes
  const stateValue = useMemo(() => ({
    files,
    selectedIndices,
    primaryIndex,
    undoStack,
    redoStack,
    pendingChanges,
    timezone,
    calendarSelectedDate,
    calendarSelectedHour,
    calendarPanelExpanded,
    calendarViewMode,
    editorRightPanelOpen,
    calendarRightPanelOpen,
    editorMobileLeftOpen,
    editorMobileRightOpen,
    calendarMobileLeftOpen,
    calendarMobileRightOpen,
    selectedCellRange,
    selectionAnchor,
    selectionEnd,
    focusedCell,
    locationModalOpen,
    presetModalOpen,
    keyboardShortcutsModalOpen,
    keyboardShortcutsInitialView,
    fileListSort,
    fileListFilters,
    tableVisibleRowMap
  }), [
    files,
    selectedIndices,
    primaryIndex,
    undoStack,
    redoStack,
    pendingChanges,
    timezone,
    calendarSelectedDate,
    calendarSelectedHour,
    calendarPanelExpanded,
    calendarViewMode,
    editorRightPanelOpen,
    calendarRightPanelOpen,
    editorMobileLeftOpen,
    editorMobileRightOpen,
    calendarMobileLeftOpen,
    calendarMobileRightOpen,
    selectedCellRange,
    selectionAnchor,
    selectionEnd,
    focusedCell,
    locationModalOpen,
    presetModalOpen,
    keyboardShortcutsModalOpen,
    keyboardShortcutsInitialView,
    fileListSort,
    fileListFilters,
    tableVisibleRowMap
  ]);

  // Actions context - stable because setters never change
  const actionsValue = useMemo(() => ({
    setFiles,
    setSelectedIndices,
    setPrimaryIndex,
    setUndoStack,
    setRedoStack,
    setPendingChanges,
    setTimezone,
    setCalendarSelectedDate,
    setCalendarSelectedHour,
    setCalendarPanelExpanded,
    setCalendarViewMode,
    setEditorRightPanelOpen,
    setCalendarRightPanelOpen,
    setEditorMobileLeftOpen,
    setEditorMobileRightOpen,
    setCalendarMobileLeftOpen,
    setCalendarMobileRightOpen,
    setSelectedCellRange,
    setSelectionAnchor,
    setSelectionEnd,
    setFocusedCell,
    setLocationModalOpen,
    setPresetModalOpen,
    setKeyboardShortcutsModalOpen,
    setKeyboardShortcutsInitialView,
    setFileListSort,
    setFileListFilters,
    setTableVisibleRowMap
  }), []);

  return (
    <AppStateContext.Provider value={stateValue}>
      <AppActionsContext.Provider value={actionsValue}>
        {children}
      </AppActionsContext.Provider>
    </AppStateContext.Provider>
  );
};

// Hook for state values (components using this will re-render when state changes)
export const useAppState = () => {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppState must be used within AppProvider');
  }
  return context;
};

// Hook for setters (components using this will NOT re-render when state changes)
export const useAppActions = () => {
  const context = useContext(AppActionsContext);
  if (!context) {
    throw new Error('useAppActions must be used within AppProvider');
  }
  return context;
};

// Legacy hook that returns both (for backward compatibility)
// Note: Using this will cause re-renders on any state change
export const useAppContext = () => {
  const state = useContext(AppStateContext);
  const actions = useContext(AppActionsContext);
  if (!state || !actions) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return { ...state, ...actions };
};
